import React from 'react';
import { WhatsAppMessage, PreviewOptions, ChatStats } from '../types/preview';
import { format } from 'date-fns';
import { de } from 'date-fns/locale';

interface ChatPreviewProps {
  messages: WhatsAppMessage[];
  options: PreviewOptions;
  stats: ChatStats;
  isFreeVersion: boolean;
}

interface FormattedMessage extends WhatsAppMessage {
  showDate: boolean;
  date: string;
  timestamp: string;
  positionClass: string;
  participantIndex: number;
  displayContent: string;
  isSystem: boolean;
  showMedia: boolean;
  showText: boolean;
  isEditedLocal?: boolean;
}

export function ChatPreview({ messages, options, stats, isFreeVersion }: ChatPreviewProps) {
  // Filter messages based on options
  const filteredMessages = messages.filter(msg => {
    if (msg.message_type === 'system' && options.showSystemMessages === false) {
      return false;
    }
    if (!msg.content?.trim() && !msg.is_media) {
      return false;
    }
    if (msg.sender !== 'System' && options.selectedSenders.length > 0 && !options.selectedSenders.includes(msg.sender)) {
      return false;
    }
    if (msg.is_media && options.mediaFilter.length > 0) {
      const mediaType = msg.message_type as 'image' | 'video' | 'audio' | 'document';
      if (!options.mediaFilter.includes(mediaType)) return false;
    }
    if (options.dateRange.from || options.dateRange.to) {
      const msgDate = new Date(msg.timestamp);
      if (options.dateRange.from && msgDate < options.dateRange.from) return false;
      if (options.dateRange.to && msgDate > options.dateRange.to) return false;
    }
    if (options.searchText) {
      const search = options.searchText.toLowerCase();
      if (!msg.content.toLowerCase().includes(search)) return false;
    }
    // Filter out media messages that are completely invisible:
    // When mediaMode is 'hidden', only keep the message if it has meaningful text
    if (msg.is_media && options.mediaMode === 'hidden') {
      let textContent = msg.content || '';
      textContent = textContent.replace(/\s*<Diese Nachricht wurde bearbeitet\.>\s*$/i, '');
      textContent = textContent.replace(/^‎[^\n]+\(Datei angehängt\)\s*/i, '');
      textContent = textContent.replace(/^\s*\n+/, '');
      if (!textContent.trim()) return false;
    }
    return true;
  });

  const displayMessages = isFreeVersion 
    ? filteredMessages.slice(0, 50) 
    : filteredMessages;

  const hasMoreMessages = isFreeVersion && filteredMessages.length > 50;

  // Build participant map from ALL messages (not filtered)
  // so that participant indices stay stable regardless of active filters
  const participantMap: { [key: string]: number } = {};
  let participantIndex = 0;
  messages.forEach(msg => {
    if (msg.sender !== 'System' && !(msg.sender in participantMap)) {
      participantMap[msg.sender] = participantIndex;
      participantIndex++;
    }
  });

  // Determine exporteur (first non-system participant from stats or first in map)
  const exporteur = stats.exporteur || Object.keys(participantMap)[0] || '';

  let lastDate: Date | null = null;

  // Format message for display
  const formatMessage = (msg: WhatsAppMessage): FormattedMessage => {
    const msgDate = new Date(msg.timestamp);
    const currentDate = msgDate.toDateString();
    const showDate = options.showDateHeaders && currentDate !== lastDate?.toDateString();
    if (showDate) lastDate = msgDate;

    const isSystem = msg.message_type === 'system';
    
    // Exporteur = sent (right side), others = received (left side)
    // This matches PDF logic: first participant is sent
    const isExporteur = msg.sender === exporteur;
    const positionClass = isSystem ? 'system' : (isExporteur ? 'sent' : 'received');
    const pIdx = isSystem ? -1 : (participantMap[msg.sender] ?? 1);

    // Format content - strip media attachment text like PDF does
    let content = msg.content || '';
    
    const isEditedLocal = msg.is_edited || /<Diese Nachricht wurde bearbeitet\.>/i.test(content);
    content = content.replace(/\s*<Diese Nachricht wurde bearbeitet\.>\s*$/i, '');
    
    // For media messages, extract clean text (without "Datei angehängt")
    let cleanContent = content;
    if (msg.is_media) {
      // Remove the "‎FILENAME (Datei angehängt)" line if present
      cleanContent = content.replace(/^‎[^\n]+\(Datei angehängt\)\s*/i, '');
      cleanContent = cleanContent.replace(/^\s*\n+/, ''); // Remove leading newlines
    }

    // Determine what to show based on mediaMode
    const showMedia = msg.is_media && options.mediaMode !== 'hidden';
    const showText = !msg.is_media || 
                     options.mediaMode === 'both' ||
                     options.mediaMode === 'hidden' ||
                     cleanContent.trim().length > 0;

    return {
      ...msg,
      showDate,
      date: format(msgDate, 'EEEE, d. MMMM yyyy', { locale: de }),
      timestamp: format(msgDate, 'HH:mm', { locale: de }),
      positionClass,
      participantIndex: pIdx,
      displayContent: cleanContent,
      isSystem,
      showMedia,
      showText,
      isEditedLocal
    };
  };

  const formattedMessages = displayMessages.map(formatMessage);

  // Escape HTML for safe rendering
  const escapeHtml = (text: string): string => {
    return text
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
  };

  const thumbnailMaxWidth = options.thumbnailSize === 'small' ? '100px' : options.thumbnailSize === 'large' ? '250px' : '150px';

  // Helper for generating darker border (equivalent to python: r-30, g-30, b-30)
  const getDarkerColor = (hex: string) => {
    if (!hex) return '#cccccc';
    hex = hex.replace('#', '');
    if (hex.length === 3) hex = hex.split('').map(c => c + c).join('');
    let r = parseInt(hex.substring(0, 2), 16);
    let g = parseInt(hex.substring(2, 4), 16);
    let b = parseInt(hex.substring(4, 6), 16);
    if (isNaN(r) || isNaN(g) || isNaN(b)) return '#cccccc';
    
    r = Math.max(0, r - 30);
    g = Math.max(0, g - 30);
    b = Math.max(0, b - 30);
    return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
  };

  const PARTICIPANT_COLORS = [
    options.otherColor || '#ffffff',      // White (Received default)
    '#e3f2fd',  // Light blue
    '#fff3e0',  // Light orange
    '#f3e5f5',  // Light purple
    '#e8f5e9',  // Light mint
    '#fff9c4',  // Light yellow
    '#e1f5fe',  // Light cyan
    '#fce4ec',  // Light pink
    '#f1f8e9',  // Light lime
    '#ede7f6',  // Light violet
  ];

  const getBubbleStyle = (msg: FormattedMessage): React.CSSProperties => {
    let bgColor = '';
    if (msg.isSystem) {
      bgColor = options.systemColor || 'rgba(225, 245, 254, 0.92)';
    } else if (msg.positionClass === 'sent') {
      bgColor = options.exporteurColor || '#dcf8c6';
    } else {
      // For 2-person chats use otherColor; for group chats cycle through palette
      const nonExporteurCount = Object.keys(participantMap).filter(
        p => p !== exporteur
      ).length;
      if (nonExporteurCount <= 1) {
        bgColor = options.otherColor || '#ffffff';
      } else {
        const colorIndex = Math.max(0, msg.participantIndex) % PARTICIPANT_COLORS.length;
        bgColor = PARTICIPANT_COLORS[colorIndex];
      }
    }
    
    return {
      backgroundColor: bgColor,
      borderRadius: options.bubbleStyle === 'square' ? '3px' : '7px',
      border: `1px solid ${getDarkerColor(bgColor)}`,
    };
  };

  return (
    <div
      className={`chat-preview ${options.bubbleSize === 'compact' ? 'compact' : ''} ${options.bubbleSize === 'wide' ? 'wide' : ''} ${options.fontSize === 'small' ? 'small-font' : ''} ${options.fontSize === 'large' ? 'large-font' : ''} ${!options.showSystemMessages ? 'hide-system' : ''}`}
      style={{
        fontSize: options.fontSize === 'small' ? '13px' : options.fontSize === 'large' ? '17px' : '15px',
        lineHeight: options.lineHeight === 'compact' ? 1.3 : options.lineHeight === 'wide' ? 1.8 : 1.5,
        backgroundColor: options.backgroundColor || '#e5ddd5',
        backgroundImage: options.showWhatsAppBackground ? 'url("https://user-images.githubusercontent.com/15075759/28719144-86dc0f70-73b1-11e7-911d-60d70fcded21.png")' : 'none',
        backgroundRepeat: 'repeat',
        backgroundBlendMode: 'overlay',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif'
      }}
    >
      {isFreeVersion && (
        <div className="preview-watermark">
          ChatArchiv.de PREVIEW
        </div>
      )}

      {options.showHeader && (
        <div className="preview-export-header" style={{
          background: '#075e54',
          color: 'white',
          padding: '15px 20px',
          margin: '-20px -20px 20px -20px',
          borderBottom: '1px solid #054c44'
        }}>
          <h1 style={{ margin: 0, fontSize: '18px', fontWeight: 500 }}>{options.exportTitle || 'WhatsApp Chat Export'}</h1>
          {(options.showHeaderParticipants || options.showHeaderStats) && (
            <div style={{ fontSize: '12px', opacity: 0.8, marginTop: '5px' }}>
              {options.showHeaderParticipants && stats.participants.length > 0 && `${stats.participants.length} Teilnehmer`}
              {options.showHeaderParticipants && options.showHeaderStats && ` • `}
              {options.showHeaderStats && `${stats.totalMessages} Nachrichten`}
              {options.showHeaderStats && stats.dateRange.start && ` • ${format(stats.dateRange.start, 'dd.MM.yyyy')} bis ${format(stats.dateRange.end, 'dd.MM.yyyy')}`}
              {options.showHeaderStats && stats.mediaCount > 0 && ` • ${stats.mediaCount} Medien`}
            </div>
          )}
        </div>
      )}

      <div className="chat-messages">
        {formattedMessages.map((msg) => (
          <div key={msg.id}>
            {/* Date Header */}
            {msg.showDate && (
              <div className="date-header">
                <span>{msg.date}</span>
              </div>
            )}

            {/* Message */}
            <div className={`message ${msg.positionClass} participant-${msg.participantIndex}`}>
              <div className="message-content" style={getBubbleStyle(msg)}>
                {/* Sender name (not for system) */}
                {!msg.isSystem && options.showSenderNames && (
                  <div className="sender">{msg.sender}</div>
                )}

                {/* Media */}
                {msg.showMedia && (
                  <div className="media-container">
                    {/* Visual Thumbnails logic */}
                    {options.mediaMode !== 'link' && (
                      <>
                        {/* Images */}
                        {msg.message_type === 'image' && (
                          msg.media_url ? (
                            <img
                              src={msg.media_url}
                              alt={msg.media_file || 'Bild'}
                              className="media-thumbnail"
                              style={{ maxWidth: thumbnailMaxWidth }}
                              onError={(e) => {
                                (e.target as HTMLImageElement).src = '/placeholder-image.png';
                              }}
                            />
                          ) : (
                            <img
                              src="/placeholder-image.png"
                              alt="Bild"
                              className="media-thumbnail"
                              style={{ maxWidth: thumbnailMaxWidth }}
                            />
                          )
                        )}

                        {/* Videos */}
                        {msg.message_type === 'video' && (
                          <div className="video-container">
                            <img
                              src={msg.media_thumb_url || '/placeholder-video.png'}
                              alt="Video Vorschau"
                              className="media-thumbnail"
                              style={{ maxWidth: thumbnailMaxWidth }}
                              onError={(e) => {
                                (e.target as HTMLImageElement).src = '/placeholder-video.png';
                              }}
                            />
                            {options.showVideoOverlay && (
                              <div className="video-overlay">
                                <div className="play-button">▶</div>
                              </div>
                            )}
                          </div>
                        )}
                      </>
                    )}

                    {/* Media File Link logic */}
                    {(options.mediaMode === 'link' || options.mediaMode === 'both' || msg.message_type === 'audio' || msg.message_type === 'document') && msg.media_file && (
                      <div className="media-filename" style={{ marginTop: '5px' }}>
                        <a href={msg.media_url || '#'} target="_blank" rel="noopener noreferrer" style={{ color: msg.positionClass === 'sent' ? '#000000' : '#027eb5', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '4px' }}>
                          {msg.message_type === 'audio' ? '🎵' : msg.message_type === 'document' ? '📄' : '🔗'} {msg.media_file}
                        </a>
                      </div>
                    )}
                  </div>
                )}

                <div className="text-time-wrapper">
                  {msg.showText && msg.displayContent.trim() && (
                    <span 
                      className="text"
                      dangerouslySetInnerHTML={{
                        __html: escapeHtml(msg.displayContent)
                          .replace(/\n/g, '<br>')
                          .replace(/(https?:\/\/\S+)/g, '<a href="$1" target="_blank">$1</a>')
                      }}
                    />
                  )}
                  {options.showTimestamps && !msg.isSystem && (
                    <span className="time-wrapper">
                      {options.showEditMarkers && msg.isEditedLocal && (
                        <span className="edited-marker">Bearbeitet</span>
                      )}
                      <span className="timestamp">{msg.timestamp}</span>
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>
        ))}

        {hasMoreMessages && (
          <div className="premium-teaser">
            <div className="teaser-content">
              <div className="teaser-fade"></div>
              <p>+{filteredMessages.length - 50} weitere Nachrichten</p>
              <button className="teaser-button">
                Für €4.99 freischalten →
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
